import {Component, EventEmitter, Input, Output} from '@angular/core';
import {CdkDragDrop, moveItemInArray} from '@angular/cdk/drag-drop';
import {ComponentDetails} from '@emm/common-items/models/component.model';
import {Variation} from '@emm/common-items/models/variation.model';

@Component({
    selector: 'emm-components-card',
    templateUrl: './components-card.component.html',
    styleUrls: ['./components-card.component.scss']
})
export class ComponentsCardComponent {
    @Input() componentCardLabel: string;
    private _components: ComponentDetails[] = [];
    @Input() set components(value: ComponentDetails[] | string[] | null | undefined) {
        // Coerce falsy / non-array values to empty array
        if (Array.isArray(value)) {
            this._components = value as ComponentDetails[];
        } else {
            this._components = [];
        }
        // Reset pending changes when new components are loaded
        this.hasPendingChanges = false;
        this.originalComponents = [];
        this.pendingOrderedTags = [];
    }

    get components(): ComponentDetails[] {
        return this._components;
    }

    @Input() variation: Variation;
    @Input() itemName: string;
    @Input() isReadOnly = false;
    @Input() hideButtons = false;
    @Input() isSuperAdmin = false;
    @Input() isItem = false;
    @Input() isItemGrouping = false;
    @Input() isModifierGrouping = false;

    @Output() addComponent = new EventEmitter<void>();
    @Output() viewComponent = new EventEmitter<ComponentDetails | string>();
    @Output() reorderComponents = new EventEmitter<string[]>();

    hasPendingChanges = false;
    private originalComponents: ComponentDetails[] = [];
    private pendingOrderedTags: string[] = [];

    onAddComponent() {
        this.addComponent.emit();
    }

    onViewComponent(component: ComponentDetails | string) {
        this.viewComponent.emit(component);
    }

    onDrop(event: CdkDragDrop<ComponentDetails[]>) {
        if (event.previousIndex !== event.currentIndex) {
            // Save original order if this is the first change
            if (!this.hasPendingChanges) {
                this.originalComponents = [...this._components];
            }

            const componentsCopy = [...this._components];
            moveItemInArray(componentsCopy, event.previousIndex, event.currentIndex);
            this._components = componentsCopy;

            // Store the pending order
            this.pendingOrderedTags = componentsCopy.map(c => this.getComponentTag(c));
            this.hasPendingChanges = true;
        }
    }

    onSaveOrder() {
        if (this.hasPendingChanges && this.pendingOrderedTags.length > 0) {
            this.reorderComponents.emit(this.pendingOrderedTags);
            this.hasPendingChanges = false;
            this.originalComponents = [];
            this.pendingOrderedTags = [];
        }
    }

    onCancelReorder() {
        if (this.hasPendingChanges) {
            this._components = [...this.originalComponents];
            this.hasPendingChanges = false;
            this.originalComponents = [];
            this.pendingOrderedTags = [];
        }
    }

    get showAddButton(): boolean {
        return !this.hideButtons && this.isSuperAdmin && (this.isItem || this.isItemGrouping || this.isModifierGrouping);
    }

    getComponentTag(component: ComponentDetails | string): string {
        if (typeof component === 'string') {
            return component;
        }
        return component?.tag ?? '';
    }
}
